<script setup>
/* Reusable Empty State Component
   Props:
   - tKey: translation key (maps to internal dictionary) IF message not provided
   - message: direct string override
   - icon: one of 'doc','target','chart','inbox','flag','light','search','activity'
   - size: 'md' | 'sm' (default md)
   - variant: 'default' | 'ghost' | 'inline'
   - dense: boolean – tighter padding
*/
import { computed } from 'vue';
import { MESSAGES as GLOBAL_MESSAGES } from '@/constants/messages.js'

const props = defineProps({
  tKey: { type: String, default: 'no_data' },
  message: { type: String, default: '' },
  icon: { type: String, default: 'doc' },
  size: { type: String, default: 'md' },
  variant: { type: String, default: 'default' },
  dense: { type: Boolean, default: false },
  pulse: { type: Boolean, default: false },
  spinner: { type: Boolean, default: false }
});

// Local minimal fallback (only used if key not present in global dict)
const LOCAL_FALLBACK = Object.freeze({
  no_data: 'Keine Daten verfügbar!'
});

const resolvedMessage = computed(() => props.message || GLOBAL_MESSAGES[props.tKey] || LOCAL_FALLBACK[props.tKey] || props.tKey);
const sizeClass = computed(()=> props.size === 'sm' ? 'empty-state--sm' : '' );
const variantClass = computed(()=> {
  if(props.variant==='ghost') return 'empty-state--ghost';
  if(props.variant==='inline') return 'empty-state--inline';
  return '';
});
const denseClass = computed(()=> props.dense ? 'empty-state--dense' : '');
const pulseClass = computed(()=> props.pulse ? 'empty-state--pulse' : '');

const iconPath = computed(()=>{
  switch(props.icon){
    case 'target': return 'M12 2a10 10 0 1 0 10 10A10.011 10.011 0 0 0 12 2Zm0 18a8 8 0 1 1 8-8a8.009 8.009 0 0 1-8 8Zm0-14a6 6 0 1 0 6 6a6.006 6.006 0 0 0-6-6Zm0 10a4 4 0 1 1 4-4a4 4 0 0 1-4 4Zm1-5a1 1 0 1 0-1 1a1 1 0 0 0 1-1Z';
    case 'chart': return 'M3 3h2v18H3zm8 6h2v12h-2zm8-4h2v16h-2z';
    case 'inbox': return 'M4 4h16v8h-4l-2 4h-4l-2-4H4z';
    case 'flag': return 'M5 3v18l7-3l7 3V3l-7-2Z';
    case 'light': return 'M12 2a5 5 0 0 0-5 5c0 1.7.8 3.2 2 4.1V15a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1v-3.9c1.2-.9 2-2.4 2-4.1a5 5 0 0 0-5-5Zm-1 18h2v2h-2z';
    case 'search': return 'M11 2a9 9 0 1 0 5.3 16.3l4.7 4.7l1.4-1.4l-4.7-4.7A9 9 0 0 0 11 2Zm0 16a7 7 0 1 1 7-7a7 7 0 0 1-7 7Z';
    case 'activity': return 'M3 3h2l3.6 9.6L12 8l3 4h6v2h-7l-2-3l-3 4l-3.4-9H3Z';
    case 'doc':
    default: return 'M6 2h7l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Zm7 6h5l-5-5Z';
  }
});
</script>

<template>
  <transition name="fade-empty" appear>
    <div class="empty-state" :class="[sizeClass, variantClass, denseClass, pulseClass, spinner ? 'is-spinner' : '']" role="status">
      <template v-if="spinner">
        <span class="es-spinner" aria-hidden="true"></span>
      </template>
      <template v-else>
        <svg class="empty-icon" viewBox="0 0 24 24" aria-hidden="true"><path :d="iconPath" fill="currentColor" /></svg>
      </template>
      <span class="empty-text">{{ resolvedMessage }}</span>
    </div>
  </transition>
</template>

<style lang="scss" scoped>
.empty-state { position:relative; height: 100%;}
.empty-icon { width:18px; height:18px; opacity:.55; flex-shrink:0; }
.empty-state--sm .empty-icon { width:15px; height:15px; }
.empty-state--dense { padding:.45rem .6rem; min-height:0; }
.empty-state--pulse .empty-icon { animation: emptyPulse 2.4s ease-in-out infinite; }
/* Spinner mode */
.empty-state.is-spinner { gap:.75rem; }
.es-spinner { width:18px; height:18px; border:3px solid currentColor; border-bottom-color:transparent; border-radius:50%; display:inline-block; animation: esSpin 0.8s linear infinite; opacity:.8; }
.empty-state--sm .es-spinner { width:15px; height:15px; border-width:2.5px; }
@keyframes esSpin { to { transform:rotate(360deg); } }
@keyframes emptyPulse { 0%,100% { opacity:.45; transform:scale(.96); } 50% { opacity:.9; transform:scale(1); } }
.fade-empty-enter-from, .fade-empty-leave-to { opacity:0; transform:translateY(4px); }
.fade-empty-enter-active, .fade-empty-leave-active { transition:opacity .25s ease, transform .25s ease; }
</style>
